import * as React from 'react';
import { useState, useContext } from 'react';
import Button from '@mui/material/Button';
import { SvgIcon } from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ImageIcon from '@mui/icons-material/Image';
import AttachmentIcon from '@mui/icons-material/Attachment';
import IconButton from '@mui/material/IconButton';
import CancelIcon from '@mui/icons-material/Cancel';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import GrainIcon from '@mui/icons-material/Grain';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { useTranslation } from "react-i18next";
import config from '../config.js';
import { useSnackbar } from 'notistack';
import { alpha } from '@mui/material/styles';
import { formatSizeToGb } from '../utils/fileManagement';
import { fileExistsInS3 } from "../utils/interfaceS3";
import { AuthContext } from "../context/AuthContext";
import { CloudContext } from "../context/CloudContext";
import { AppConfigContext } from "../context/AppConfigContext";
import { FileUploader } from "react-drag-drop-files";
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';

import {readLazHeaderInfo} from "../utils/lazHeaderReader.js"

import HelpOutlineIcon from '@mui/icons-material/HelpOutlineOutlined';
import ProcessingInfoDialog from './ProcessingHelpDialog';
import EpsgSelectionDialog from './EpsgSelectionDialog';


import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';

export default function NewDataDialog({
  setOpen,
  open,
  projectSelected,
  userId,
  reloadProjects,
  addToUploadQueue,
  GoUploadedThisMonth,
  maxGoUploadPerMonth,
  processings, // <-- This prop contains the fetched processing data.
}) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { user, idToken, service } = useContext(AuthContext);
  const { region, idpool } = useContext(CloudContext);
  const { maxFileSize } = useContext(AppConfigContext);
  const isMappingService = service === 'mapping';

  const [fileSelected, setFileSelected] = useState("");
  const [description, setDescription] = useState("");
  const [imageSelected, setImageSelected] = useState("");
  const [archiveSelected, setArchiveSelected] = useState("");
  const [descriptionArchive, setDescriptionArchive] = useState("");
  const [photosSelected, setPhotosSelected] = useState([]);
  // Controlled selection state for the tree view.
  const [selectedItems, setSelectedItems] = useState([]);
  // Controlled expansion state for the tree view.
  const [expandedItems, setExpandedItems] = useState([]);
  // EPSG selection dialog state
  const [epsgDialogOpen, setEpsgDialogOpen] = useState(false);
  const [epsgDialogSession, setEpsgDialogSession] = useState(0);
  const [currentLazHeader, setCurrentLazHeader] = useState(null);
  const [selectedEpsgData, setSelectedEpsgData] = useState(null);

  const fileTypes = ["LAS", "LAZ", "las", "laz"];
  const handleChange = async (file) => {
    setFileSelected(file);
    setSelectedEpsgData(null);
    setCurrentLazHeader(null);

    // Georeferencing only matters in 'mapping' service: the EPSG selection is used
    // to place the dataset in the Cesium viewer. In 'classic' service the point cloud
    // is displayed in its own coordinate system, so unreferenced files are accepted
    // and the EPSG dialog is skipped.
    if (!isMappingService) {
      return;
    }

    try {
      const header = await readLazHeaderInfo(file);
      console.log(header);

      // Check if the file contains geo reference information
      if (!header.wkt && !header.epsg) {
        enqueueSnackbar(t('ErrorNoGeoReference'), { variant: 'error' });
        setFileSelected('');
        setCurrentLazHeader(null);
        return;
      }

      setCurrentLazHeader(header);
      setEpsgDialogSession(session => session + 1);
      setEpsgDialogOpen(true);
    } catch (err) {
      console.error('Error reading LAZ header:', err);
      enqueueSnackbar(t('ErrorReadingHeader'), { variant: 'warning' });
    }
  };

  const handleEpsgConfirm = (epsgData) => {
    setSelectedEpsgData(epsgData);
    setEpsgDialogOpen(false);
    console.log('EPSG data confirmed:', epsgData);
  };

  const handleEpsgClose = () => {
    setEpsgDialogOpen(false);
    // Clear file selection if user cancels EPSG selection
    setFileSelected('');
    setCurrentLazHeader(null);
  };

  const imageTypes = ["PNG", "png", "JPG", "jpg", "JPEG", "jpeg"];
  const handleImageChange = (file) => {
    setImageSelected(file);
  };

  const archiveTypes = ["ZIP", "zip"];
  const handleArchiveChange = (file) => {
    setArchiveSelected(file);
    console.log(file)
  };

  const handlePhotosChange = (files) => {
    if (!files) {
      return;
    }
    const isFileList = typeof FileList !== 'undefined' && files instanceof FileList;
    const filesArray = Array.isArray(files)
      ? files
      : isFileList
        ? Array.from(files)
        : [files];
    setPhotosSelected((prev) => [...prev, ...filesArray]);
  };

  const handleRemovePhoto = (indexToRemove) => {
    setPhotosSelected((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const formatSizeInMo = (bytes) => {
    if (typeof bytes !== 'number' || Number.isNaN(bytes)) {
      return '';
    }
    return `${(bytes / (1024 * 1024)).toFixed(2)} Mo`;
  };

  const handleTypeError = () => {
    enqueueSnackbar(`${t("ErrorType")}`, { variant: "warning" });
  };

  const handleClose = () => {
    setOpen(false);
    clearFields();
  };

  function hasWhiteSpace(s) {
    return s.indexOf(" ") >= 0;
  }

  async function fileAlreadyExists(filename) {
    const fileExists = await fileExistsInS3(
      user.sub,
      idToken,
      `${config.region}`,
      idpool,
      filename
    );
    return fileExists;
  }

  function clearFields() {
    setFileSelected("");
    setImageSelected("");
    setPhotosSelected([]);
    setDescription("");
    setSelectedItems([]);
    setExpandedItems([]);
    setArchiveSelected("");
    setDescriptionArchive("");
    setCurrentLazHeader(null);
    setSelectedEpsgData(null);
  }

  function extractIdsByTitles(inputTitles, processings) {
    const titleSet = new Set(inputTitles);
    const result = [];

    processings.forEach(proc => {
      // Check if the parent processing is selected.
      const processingSelected = titleSet.has(proc.title);

      // Get the IDs of all subprocessings that are selected.
      const selectedSubIds = (proc.subProcessings || [])
        .filter(sub => titleSet.has(sub.title))
        .map(sub => sub.id);

      // If either the processing or at least one subprocessing is selected,
      // include this processing in the result.
      if (processingSelected || selectedSubIds.length > 0) {
        result.push({
          processing: proc.id,
          subprocessings: selectedSubIds,
        });
      }
    });

    return result;
  }

  const handleCreateArchive = async () => {
    if (!archiveSelected) {
      enqueueSnackbar(t("ErrorNoArchive"), { variant: "warning" });
      return;
    }

    if (hasWhiteSpace(archiveSelected.name)) {
      enqueueSnackbar(t("ErrorFileNoSpace"), { variant: "warning" });
      return;
    }

    const archiveSize = formatSizeToGb(archiveSelected.size);
    if ((+GoUploadedThisMonth + archiveSize) > maxGoUploadPerMonth) {
      enqueueSnackbar(t("MaxUploadReached"), { variant: "warning" });
      return;
    }

    const uploadItem = {
      type: 'archive',
      file: archiveSelected,
      description: descriptionArchive,
    };

    addToUploadQueue([uploadItem]);
    setOpen(false);
    clearFields();
  };

  const handleCreate = async () => {
    // Handle archive tab separately
    if (tabValue === 'archive') {
      await handleCreateArchive();
      return;
    }

    const hasPointCloud = Boolean(fileSelected);
    const photoCount = photosSelected.length;

    if (!hasPointCloud && photoCount === 0) {
      enqueueSnackbar(`${t("ErrorNoFileOrPhotos")}`, { variant: "warning" });
      return;
    }

    if (photoCount > 0 && photoCount < 3) {
      enqueueSnackbar(`${t("ErrorMinimumPhotos")}`, { variant: "warning" });
      return;
    }

    const pointCloudSize = hasPointCloud ? formatSizeToGb(fileSelected.size) : 0;
    const photosSize = photosSelected.reduce(
      (total, photo) => total + formatSizeToGb(photo.size || 0),
      0
    );

    if (hasPointCloud) {
      if(!isMappingService){
        const exists = await fileAlreadyExists(fileSelected.name);
        if (exists) {
          enqueueSnackbar(`${t("ErrorAlreadyExists")}`, { variant: "warning" });
          clearFields();
          return;
        }
      }
      if (hasWhiteSpace(fileSelected.name)) {
        enqueueSnackbar(`${t("ErrorFileNoSpace")}`, { variant: "warning" });
        clearFields();
        return;
      }
    }

    if ((+GoUploadedThisMonth + pointCloudSize + photosSize) > maxGoUploadPerMonth) {
      enqueueSnackbar(`${t("MaxUploadReached")}`, { variant: "warning" });
      clearFields();
      return;
    }

    const processingList = extractIdsByTitles(selectedItems, processings);
    //console.log(processingList);
    //console.log('Subprocessing IDs:', subProcessingIds);

    const uploadItem = {
      file: fileSelected,
      imageFile: imageSelected,
      description: description,
      processings: processingList,
      photos: photosSelected,
      epsgData: selectedEpsgData,
    };

    console.log("adding to queue")

    addToUploadQueue([uploadItem]);
    setOpen(false);
    clearFields();
  };

  // Controlled selection handler.
  const handleSelectedItemsChange = (event, ids) => {
    let newSelected = [...ids];
    let newExpanded = [...ids];

    if (
      newSelected.includes("forest-report") &&
      !newExpanded.some((item) => item.includes("forest-report"))
    ) {
      newExpanded.push("forest-report");
    }

    if (selectedItems.includes("forest-report") && !newSelected.includes("forest-report")) {
      newSelected = newSelected.filter((item) => !item.startsWith("forest-report-"));
    }

    setExpandedItems(newExpanded);
    setSelectedItems(newSelected);
  };

  // Helper function to create a valid itemId from a title.
  const createItemId = (title) =>
    title.toLowerCase().replace(/\s+/g, '-');


  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [currentProcessing, setCurrentProcessing] = useState(null);
  const [tabValue, setTabValue] = useState('pointCloud');

  const handleOpenInfo = proc => {
    setCurrentProcessing(proc);
    setInfoDialogOpen(true);
  };
  const handleCloseInfo = () => {
    setInfoDialogOpen(false);
    setCurrentProcessing(null);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const renderPointCloudSection = () => (
    <>
      <DialogContent>
        <DialogContentText component="div">
          <Typography color="text.secondary" variant="body2">
            {t("SelectALASFile")}
          </Typography>
        </DialogContentText>
        <Stack spacing={2} sx={{ mt: 3 }}>
          {/* File Uploader for LAS/LAZ Files */}
          <Stack spacing={1}>
            <Typography color="text.secondary" variant="h6">
              {t("LASLAZFile")}
            </Typography>
            <FileUploader
              handleChange={handleChange}
              onTypeError={handleTypeError}
              multiple={false}
              name="file"
              types={fileTypes}
              hoverTitle=" "
              maxSize={maxFileSize * 1000}
              children={
                <Box>
                  <Paper
                    variant="outlined"
                    sx={{
                      borderColor: "#E5E7EB",
                      borderStyle: "solid",
                      borderWidth: 1,
                      "&:hover": { backgroundColor: alpha("#111927", 0.04) },
                    }}
                  >
                    <Grid
                      container
                      direction="row"
                      style={{ minHeight: "80px", padding: 20 }}
                      sx={{ alignItems: 'center', justifyContent: 'center' }}
                    >
                      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                        <CloudUploadIcon style={{ color: "gray" }} />
                        <Typography variant="body2" color="text.secondary">
                          {t("dragndrop")}
                        </Typography>
                      </Stack>
                    </Grid>
                  </Paper>
                </Box>
              }
            />
            {fileSelected && (
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <AttachmentIcon style={{ color: "gray" }} />
                <Typography variant="body2" color="text.secondary">
                  <b>{fileSelected.name}</b>
                </Typography>
              </Stack>
            )}
          </Stack>
          {/* Description Field */}
          <Stack spacing={1}>
            <Typography color="text.secondary" variant="h6">
              {t("Description")}
            </Typography>
            <TextField
              fullWidth
              name="description"
              type="text"
              multiline
              rows={2}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </Stack>
          {/* File Uploader for Image */}
          <Stack spacing={1}>
            <Typography color="text.secondary" variant="h6">
              {t("Image")}
            </Typography>
            <FileUploader
              handleChange={handleImageChange}
              multiple={false}
              name="image"
              types={imageTypes}
              hoverTitle=" "
              maxSize={10}
              children={
                <Box>
                  <Paper
                    variant="outlined"
                    sx={{
                      borderColor: "#E5E7EB",
                      borderStyle: "solid",
                      borderWidth: 1,
                      "&:hover": { backgroundColor: alpha("#111927", 0.04) },
                    }}
                  >
                    <Grid
                      container
                      direction="row"
                      style={{ minHeight: "80px", padding: 20 }}
                      sx={{ alignItems: 'center', justifyContent: 'center' }}
                    >
                      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                        <ImageIcon style={{ color: "gray" }} />
                        <Typography variant="body2" color="text.secondary">
                          {t("dragndrop")}
                        </Typography>
                      </Stack>
                    </Grid>
                  </Paper>
                </Box>
              }
            />
            {imageSelected && (
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <AttachmentIcon style={{ color: "gray" }} />
                <Typography variant="body2" color="text.secondary">
                  <b>{imageSelected.name}</b>
                </Typography>
              </Stack>
            )}
          </Stack>
          {/* Processing Options using Controlled Tree View */}
          <Stack spacing={1}>
            <Typography color="text.secondary" variant="h6">
              {t("ProcessingOptions")}
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {t("ProcessingOptionsSubtitle")}
            </Typography>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                maxHeight: 150,
                overflowY: 'auto',
                p: 1,
              }}
            >
              <SimpleTreeView
                multiSelect
                checkboxSelection
                selectedItems={selectedItems}
                onSelectedItemsChange={handleSelectedItemsChange}
                expandedItems={expandedItems}
              >
              {processings.map((processing) => {
                const parentId = createItemId(processing.title);
                if (processing.subProcessings && processing.subProcessings.length > 0) {
                  return (
                    <TreeItem
                      key={processing.id}
                      itemId={parentId}
                      sx={{
                        '& .MuiTreeItem-content': {
                          alignItems: 'flex-start',
                        },
                        '& .MuiTreeItem-label': {
                          width: '100%',
                        },
                      }}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              color="text.secondary"
                              sx={{ lineHeight: 1.4 }}
                            >
                              {t(processing.title)}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                mt: 0.25,
                                lineHeight: 1.3,
                                whiteSpace: 'normal',
                              }}
                            >
                              {t(processing.subtitle)}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            sx={{ ml: 'auto', alignSelf: 'flex-start' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenInfo(processing);
                            }}
                          >
                            <HelpOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      }
                    >
                      {processing.subProcessings.map((sub) => {
                        const subId = `${createItemId(sub.title)}`;
                        return (
                          <TreeItem
                            key={sub.id}
                            itemId={subId}
                            label={<Typography variant="body3" color="text.secondary">{t(sub.title)}</Typography>}
                          />
                        );
                      })}
                    </TreeItem>
                  );
                }
                return (
                  <TreeItem
                    key={processing.id}
                    itemId={parentId}
                    sx={{
                      '& .MuiTreeItem-content': {
                        alignItems: 'flex-start',
                      },
                      '& .MuiTreeItem-label': {
                        width: '100%',
                      },
                    }}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            color="text.secondary"
                            sx={{ lineHeight: 1.4 }}
                          >
                            {t(processing.title)}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              mt: 0.25,
                              lineHeight: 1.3,
                              whiteSpace: 'normal',
                            }}
                          >
                            {t(processing.subtitle)}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          sx={{ ml: 'auto', alignSelf: 'flex-start' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenInfo(processing);
                          }}
                        >
                          <HelpOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    }
                  />
                );
              })}
              </SimpleTreeView>
            </Box>
          </Stack>
        </Stack>
      </DialogContent>
    </>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <Box sx={{ p: 3 }}>
        <DialogTitle>
          <Typography variant="h4" component="div">
            {t("UploadData")}
          </Typography>
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={(theme) => ({
            position: 'absolute',
            right: 16,
            top: 16,
            color: theme.palette.grey[500],
          })}
        >
          <CancelIcon />
        </IconButton>
        {isMappingService ? (
          <TabContext value={tabValue}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 1 }}>
              <TabList onChange={handleTabChange} aria-label="upload data tabs">
                <Tab label={t("PointCloud")} icon={<GrainIcon />} iconPosition="start" value="pointCloud" />
                <Tab label={t("Photos")} icon={<InsertPhotoIcon />} iconPosition="start" value="photos" />
                <Tab label={t("Archive")} icon={<FolderZipIcon />} iconPosition="start" value="archive" />
              </TabList>
            </Box>
            <TabPanel value="pointCloud" sx={{ p: 0 }}>
              {renderPointCloudSection()}
            </TabPanel>
            <TabPanel value="photos" sx={{ p: 0 }}>
              <DialogContent>
                <DialogContentText component="div">
                  <Typography color="text.secondary" variant="body2">
                    {t("SelectPhotos")}
                  </Typography>
                </DialogContentText>
                <Stack spacing={2} sx={{ mt: 3 }}>
                  <Typography color="text.secondary" variant="h6">
                    {t("PhotosReconstruction")}
                  </Typography>
                  <FileUploader
                    handleChange={handlePhotosChange}
                    multiple
                    name="photos"
                    types={imageTypes}
                    hoverTitle=" "
                    maxSize={100}
                    children={
                      <Box>
                        <Paper
                          variant="outlined"
                          sx={{
                            borderColor: "#E5E7EB",
                            borderStyle: "solid",
                            borderWidth: 1,
                            "&:hover": { backgroundColor: alpha("#111927", 0.04) },
                          }}
                        >
                          <Grid
                            container
                            direction="row"
                            style={{ minHeight: "80px", padding: 20 }}
                            sx={{ alignItems: 'center', justifyContent: 'center' }}
                          >
                            <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                              <ImageIcon style={{ color: "gray" }} />
                              <Typography variant="body2" color="text.secondary">
                                {t("dragndrop")}
                              </Typography>
                            </Stack>
                          </Grid>
                        </Paper>
                      </Box>
                    }
                  />
                  {photosSelected.length > 0 && (
                    <>
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          height: 180,
                          overflowY: 'auto',
                        }}
                      >
                        <List dense disablePadding>
                          {photosSelected.map((photo, index) => (
                            <ListItem
                              key={`${photo.name}-${photo.lastModified ?? index}`}
                              secondaryAction={
                                <IconButton
                                  edge="end"
                                  aria-label={t("Delete")}
                                  onClick={() => handleRemovePhoto(index)}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              }
                            >
                              <ListItemText
                                primary={
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{ width: '100%', alignItems: 'center' }}
                                  >
                                    <Typography
                                      variant="body2"
                                      noWrap
                                      sx={{ flexGrow: 1 }}
                                    >
                                      {photo.name}
                                    </Typography>
                                    {typeof photo.size === 'number' && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        component="span"
                                      >
                                        {formatSizeInMo(photo.size)}
                                      </Typography>
                                    )}
                                  </Stack>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        {t("PhotosSelectedCount", { count: photosSelected.length })}
                      </Typography>
                    </>
                  )}
                </Stack>
              </DialogContent>
            </TabPanel>
            <TabPanel value="archive" sx={{ p: 0 }}>
              <DialogContent>
                <DialogContentText component="div">
                  <Typography color="text.secondary" variant="body2">
                    {t("SelectArchive")}
                  </Typography>
                </DialogContentText>
                <Stack spacing={2} sx={{ mt: 3 }}>
                  <Typography color="text.secondary" variant="h6">
                    {t("Archive")}
                  </Typography>
                  <FileUploader
                    handleChange={handleArchiveChange}                    
                    name="archive"
                    types={archiveTypes}
                    hoverTitle=" "
                    multiple={false}
                    maxSize={maxFileSize * 1000}
                    children={
                      <Box>
                        <Paper
                          variant="outlined"
                          sx={{
                            borderColor: "#E5E7EB",
                            borderStyle: "solid",
                            borderWidth: 1,
                            "&:hover": { backgroundColor: alpha("#111927", 0.04) },
                          }}
                        >
                          <Grid
                            container
                            direction="row"
                            style={{ minHeight: "80px", padding: 20 }}
                            sx={{ alignItems: 'center', justifyContent: 'center' }}
                          >
                            <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                              <ImageIcon style={{ color: "gray" }} />
                              <Typography variant="body2" color="text.secondary">
                                {t("dragndrop")}
                              </Typography>
                            </Stack>
                          </Grid>
                        </Paper>
                      </Box>
                    }
                  />
                  {archiveSelected && (
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <AttachmentIcon style={{ color: "gray" }} />
                    <Typography variant="body2" color="text.secondary">
                      <b>{archiveSelected.name}</b>
                    </Typography>
                  </Stack>
                )}  
                          {/* Description Field */}
                <Stack spacing={1}>
                  <Typography color="text.secondary" variant="h6">
                    {t("Description")}
                  </Typography>
                  <TextField
                    fullWidth
                    name="descriptionArchive"
                    type="text"
                    multiline
                    rows={2}
                    value={descriptionArchive}
                    onChange={(event) => setDescriptionArchive(event.target.value)}
                  />
                </Stack>
                </Stack>
              </DialogContent>
            </TabPanel>
          </TabContext>
        ) : (
          renderPointCloudSection()
        )}

      <DialogActions>
        <Button
          startIcon={
            <SvgIcon fontSize="medium">
              <FileUploadIcon />
            </SvgIcon>
          }
          variant="contained"
          onClick={handleCreate}
        >
          {t("Upload")}
        </Button>
      </DialogActions>  
      </Box>

      <ProcessingInfoDialog
        open={infoDialogOpen}
        onClose={handleCloseInfo}
        processing={currentProcessing}
      />

      {open && epsgDialogOpen && currentLazHeader && (
        <EpsgSelectionDialog
          key={epsgDialogSession}
          open={epsgDialogOpen}
          onClose={handleEpsgClose}
          onConfirm={handleEpsgConfirm}
          lazHeader={currentLazHeader}
          idToken={idToken}
          region={region}
          idpool={idpool}
        />
      )}
    </Dialog>
  );
}
